// File: typescript/apps/front-end/src/main/react/thoughts/ThoughtReplyTree.tsx
//
// Subtree loader for the threaded-comments reply rail. Fetches the whole visible subtree under a root
// argument in ONE request (down to maxDepth), builds a parent→children map, and renders it via the
// recursive ThoughtThread renderer. Because everything is loaded up front, collapse/expand is pure
// client state (instant, no refetch) and the tree never reveals levels "in turns". Beyond the fetched
// depth, ThoughtThreadNode shows "Continue this thread" (the focused permalink) instead of nesting.
//
// Collapsing the root's own thread is handled by the caller (the opening card), which renders the
// opening argument in header-only mode — the same Reddit-style full collapse as a nested reply.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ClientFactory } from "@/main/common/ClientFactory";
import { isInterruptedRequestError } from "@/main/common/ApiUtils";
import logger from "@/lib/logger";
import { PaginatedListRetryState } from "@/main/react/list/PaginatedListState";
import { Skeleton } from "@/components/ui/skeleton";
import {
    ChildrenByParent,
    ReplyTreeCommonProps,
    ThreadChildrenBlock,
    ThreadNode,
} from "@/main/react/thoughts/ThoughtThread";
import { focusThreadCollapseCounterpart } from "@/main/react/thoughts/threadCollapseFocus";

export type { ReplyTreeCommonProps } from "@/main/react/thoughts/ThoughtThread";

const DEFAULT_MAX_VISUAL_REPLY_DEPTH = 3;

export default function ThoughtReplyTree(props: {
    common: ReplyTreeCommonProps;
    rootArgumentPublicId: string;
    maxDepth?: number;
    // Reports the number of direct replies once loaded, so the opening card can draw its connector
    // and know whether it has a collapsible thread.
    onCountChange?: (count: number) => void;
    // Hover state of the opening argument's rail (its trunk line), so the first-level elbows light
    // when the opening collapse-rail is hovered — same per-rail model as nested nodes.
    railHovered?: boolean;
    // FE#QA-4: the root's reply count as the caller already knows it (the opening thought's DTO carries
    // `replyCount`). Only a root expected to have replies shows the loading skeleton, so the many cards
    // with no replies never flash one.
    expectedReplyCount?: number;
}) {
    const { common, rootArgumentPublicId, maxDepth = DEFAULT_MAX_VISUAL_REPLY_DEPTH, onCountChange, railHovered, expectedReplyCount } = props;
    const [subtree, setSubtree] = useState<ThreadNode[] | null>(null);
    // Per-node collapse state for nested replies, held here so it survives a subtree refetch.
    const [collapsedIds, setCollapsedIds] = useState<Set<string>>(() => new Set());
    // At most ONE inline composer is open per tree. See the ThreadContext field comment for why this
    // is not per-node local state. Deliberately NOT preserved across a subtree refetch: a refetch
    // follows a publish, and reopening a composer the member just submitted is not what they asked for.
    const [activeReplyPublicId, setActiveReplyPublicId] = useState<string | null>(null);
    // FE#QA-4: an actionable load failure (5xx / network / malformed) is its own state with a retry. It
    // used to write `[]`, so the thread rendered exactly as an empty one: no message, no way back.
    const [loadFailed, setLoadFailed] = useState(false);

    const loadSubtree = useCallback(async () => {
        try {
            const resp = await ClientFactory.listThoughtSubtree<ThreadNode[]>(rootArgumentPublicId, maxDepth);
            setLoadFailed(false);
            setSubtree(Array.isArray(resp.data) ? resp.data : []);
        } catch (err) {
            // The viewer navigated away / this node unmounted / a crawler bailed mid-render before
            // the response landed. Nothing failed and nothing is left to render into, so bail before
            // the status classification below rather than logging it as a load failure.
            if (isInterruptedRequestError(err)) {
                return;
            }
            // A 4xx means the root thought is gone or not visible to this viewer — an expected
            // protocol outcome that we already handle by rendering an empty thread, not a defect.
            // Only 5xx/network/malformed failures are actionable, so only those get `error`. This
            // keeps the production FRONTEND error log (and the Playwright client-error oracle in
            // tests/helpers/islandHealthGuard.mjs) meaning "something is actually broken".
            const status = (err as { response?: { status?: number } })?.response?.status;
            const expected = typeof status === "number" && status >= 400 && status < 500;
            const log = expected ? logger.warn : logger.error;
            log("[ThoughtReplyTree] failed to load subtree", err);
            // A refetch that fails after a successful load (e.g. after a publish) keeps the tree it
            // already shows rather than blanking it; only a thread with nothing to show offers Retry.
            if (!expected) {
                setLoadFailed(true);
            }
            setSubtree((prev) => (expected ? [] : (prev ?? [])));
        }
    }, [rootArgumentPublicId, maxDepth]);

    useEffect(() => {
        void loadSubtree();
    }, [loadSubtree]);

    const childrenByParent = useMemo<ChildrenByParent>(() => {
        const map: ChildrenByParent = new Map();
        for (const node of subtree ?? []) {
            const parentId = node.parentArgumentPublicId ?? "";
            if (!parentId) {
                continue;
            }
            // Defend the recursive renderer against a malformed graph: a reply can never be its own
            // parent. Such a row would make this a self-loop in childrenByParent and drive the
            // ThreadChildrenBlock↔ThoughtThreadNode recursion into an infinite stack (prod
            // RangeError, 2026-07-21). The renderer also carries an ancestor-cycle guard, but drop
            // the simplest case at the source. (Ancestor cycles are caught in the renderer.)
            if (parentId === node.publicId) {
                continue;
            }
            const existing = map.get(parentId);
            if (existing) {
                existing.push(node);
            } else {
                map.set(parentId, [node]);
            }
        }
        return map;
    }, [subtree]);

    const rootChildren = childrenByParent.get(rootArgumentPublicId) ?? [];
    const rootChildCount = rootChildren.length;

    useEffect(() => {
        if (subtree !== null) {
            onCountChange?.(rootChildCount);
        }
    }, [subtree, rootChildCount, onCountChange]);

    // FE#QA-3: the toggle to hand focus across once the new row has committed (see threadCollapseFocus.ts).
    const pendingFocusRef = useRef<{ argumentPublicId: string; collapsed: boolean } | null>(null);
    useLayoutEffect(() => {
        const pending = pendingFocusRef.current;
        if (!pending) {
            return;
        }
        pendingFocusRef.current = null;
        focusThreadCollapseCounterpart(pending.argumentPublicId, pending.collapsed);
    }, [collapsedIds]);

    const onToggleNodeCollapsed = useCallback((argumentPublicId: string, isCollapsed: boolean) => {
        pendingFocusRef.current = { argumentPublicId, collapsed: isCollapsed };
        setCollapsedIds((prev) => {
            const next = new Set(prev);
            if (isCollapsed) {
                next.add(argumentPublicId);
            } else {
                next.delete(argumentPublicId);
            }
            return next;
        });
    }, []);

    // Clicking Reply on the node that already has the composer closes it; clicking any other node moves
    // the single composer there. Unsaved text is not lost either way — thoughtDraftStore keys a draft by
    // (discussion, parent, account) and restores it when that node's composer reopens.
    const onReplyToggled = useCallback((argumentPublicId: string) => {
        setActiveReplyPublicId((prev) => (prev === argumentPublicId ? null : argumentPublicId));
    }, []);

    if (loadFailed && rootChildCount === 0) {
        return (
            <PaginatedListRetryState
                message={common.localizer("paginated.load_failed")}
                retryLabel={common.localizer("literal.retry")}
                onRetry={loadSubtree}
            />
        );
    }

    // FE#QA-4: `subtree` is null until the first load settles, and rendering nothing then made a slow
    // load look exactly like a thread with no replies. A root the caller expects to have replies shows
    // shape-only reply rows instead (aria-hidden, no copy); a loaded-empty tree still renders nothing.
    if (subtree === null && !loadFailed && (expectedReplyCount ?? 0) > 0) {
        return (
            <div aria-hidden="true" className="ml-10 pr-3 pt-2 sm:ml-12 sm:pr-4" data-playwright-id="thought-reply-tree-loading">
                {Array.from({ length: Math.min(expectedReplyCount ?? 0, 2) }, (_, row) => (
                    <div key={row} className="mb-4 flex min-w-0 gap-3">
                        <Skeleton className="size-8 shrink-0 rounded-full" />
                        <div className="min-w-0 flex-1 space-y-2 pt-1">
                            <Skeleton className="h-3 w-32 max-w-[60%]" />
                            <Skeleton className="h-3 w-full" />
                            <Skeleton className="h-3 w-3/5" />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (!subtree || rootChildCount === 0) {
        return null;
    }

    return (
        <ThreadChildrenBlock
            nodes={rootChildren}
            railHovered={railHovered}
            // Seed the ancestor-cycle guard with the root id: a "child" that points back at the root
            // (or at any ancestor, deeper down) is a cycle and is dropped rather than recursed into.
            ancestorIds={new Set([rootArgumentPublicId])}
            ctx={{
                common,
                childrenByParent,
                collapsedIds,
                onToggleCollapsed: onToggleNodeCollapsed,
                onReplyPublished: loadSubtree,
                activeReplyPublicId,
                onReplyToggled,
            }}
        />
    );
}
